import React, {
    forwardRef,
    useImperativeHandle,
    useState,
    useEffect,
    useCallback
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "../../atoms/Modal";
import {
    LOGGED_AZURE_TITLE,
    LOGIN_AZURE_TITLE,
    MESSAGES_STATUS_CHANGED,
    PURGE_CONFIRM
} from "../../../constants/contants";
import {purgeEmail, getSettings, authorizeToken} from "../../../api/emailTriage";
import loginAzure from "../LoginAzure";
import {LoadingPage} from "../../organisms/LoadingPage";
import {getUserData} from "../../../utils/common";

interface LocationState {
    service: string;
    account_name: string;
    account_email: string;
    message?: string;
    status?: boolean;
    sourceId?: string;
}

interface ModalContent {
    title: string;
    content: string;
    textAgree: string;
    variant: string;
    action?:string;
    children: React.ReactNode;
    onConfirm: () => void;
    onCancel?: () => void;
}

export const BlockList = forwardRef((_, ref) => {
    const [loading, setLoading] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [contentModal, setContentModal] = useState<ModalContent | null>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as LocationState | null;
    const currentUser = getUserData();

    useEffect(() => {
        if (locationState) {
            navigate('.', { replace: true, state: null });
        }
    }, [locationState, navigate]);

    useEffect(() => {
        if (locationState && locationState?.service?.includes('block_list')) {
            handleLoginSuccess(locationState);
            setIsValidated(true);
        }
    }, []);

    const handleCloseAllPopup = () => {
        setOpenConfirm(false);
    };

    const handleLoginSuccess = useCallback((params: LocationState) => {
        const htmlContent = `<div>You’ve logged into <strong>${params?.account_name}</strong>’s account. You can now use the Block List functionality.</div>
                    <br/>
                    <div>
                        We store Microsoft Purview authorisation for 90 days. These details will be used by PH2 Phish Focus portal for integrating the block list entries to your organisation mailboxes, you can cancel this anytime by terminating the refresh token under <strong>${params?.account_name}</strong>’s account in Azure AD. You will be required to log in again to re-authorise after the 90 days period.
                    </div>`;

        setContentModal({
            title: LOGGED_AZURE_TITLE,
            content: "",
            children: <div dangerouslySetInnerHTML={{ __html: htmlContent }} />,
            textAgree: 'OK',
            action: '',
            variant: 'success',
            onConfirm: handleCloseAllPopup
        });
        setOpenConfirm(true);
    }, []);

    const handleOpenUnauthorizedPopup = useCallback((expired: boolean, onConfirm: () => void) => {
        const contentHTML = expired
            ? `
                <div>
                    <div>Your Microsoft Purview credentials have expired. Please re-log into Microsoft 365 account with required Microsoft Purview Administrator credentials.</div>
<!--                    <div>This will allow the purging of emails from the organisation mailboxes on your behalf.</div>-->
                </div>
              `
            : `
                <div>
                    <div>To perform this action, please log into Microsoft 365 account with required Microsoft Purview Administrator credentials.</div>
<!--                    <div>This will allow the purging of emails from the organisation mailboxes on your behalf.</div>-->
                </div>
              `;

        setContentModal({
            title: LOGIN_AZURE_TITLE,
            content: "",
            children: <div dangerouslySetInnerHTML={{ __html: contentHTML }} />,
            textAgree: 'Log in',
            variant: 'default',
            onConfirm
        });
        setOpenConfirm(true);
    }, []);


    const performBlockList = useCallback(
        (
            payload: any,
            onSuccess: (result: any) => void,
            onError: () => void
        ) => {
            const { service, sourceId, data } = payload;
            const retryLogin = () => {
                handleOpenUnauthorizedPopup(false, () => {
                    setLoading(true)
                    setOpenConfirm(false)
                    loginAzure({ service, sourceId });
                });
            };

            const callBlockList = (payload:any) => {
                try {
                    setLoading(false)
                    setOpenConfirm(false)
                    onSuccess(payload)
                } catch (error: any) {
                    setLoading(false)
                }
            };

            if (!isValidated) {
                setLoading(true)
                authorizeToken({
                    "identity_action": "ensure_valid_token",
                    "app_id":  process.env.REACT_APP_PHISH_FOCUS_AZURE_APP_ID,
                    "user_id": currentUser['id'],
                    "user_role": currentUser['role'],
                    "user_email": currentUser['email'],
                    "service": "block_list",
                    "service_key": "block_list"
                }).then((response) => {
                      setLoading(false)
                      if (response?.data?.code=='SUCCESS') {
                          setIsValidated(true);
                          callBlockList(response?.data);
                      } else if (response?.data?.code=='ERROR_NONE_AUTHORIZED' ||
                          response?.data?.code=='TOKEN_EXPIRED') {
                          retryLogin();
                          onError();
                      } else {
                          onError();
                          throw('Error')
                      }
                  }).catch(() => {
                    console.error('Cannot get Block list account data');
                    setLoading(false)
                    onError();
                });
            } else {
                setLoading(false)
                callBlockList({});
            }
        },
        [isValidated, handleOpenUnauthorizedPopup]
    );

    useImperativeHandle(ref, () => ({
        performBlockList
    }));

    return (
        <div>
            <Modal
                idModal="modal-block-list-service"
                openDialog={openConfirm}
                variant={contentModal?.variant}
                title={contentModal?.title}
                content={contentModal?.content}
                actionAgree={contentModal?.onConfirm}
                actionCancel={contentModal?.onCancel || handleCloseAllPopup}
                textAgree={contentModal?.textAgree || "Accept"}
                onClose={contentModal?.onCancel ? handleCloseAllPopup : undefined}
                children={contentModal?.children}
            />
            <LoadingPage open={loading}/>
        </div>
    );
});

export default BlockList;
